/**
 * CompactRichTextEditor - A minimal rich text editor for dialogs and forms.
 *
 * Features:
 * - Single-row compact toolbar with small buttons
 * - Toolbar only appears on focus, hides on blur
 * - Same Tiptap extensions as RichTextEditor but stripped-down UI
 * - Suitable for dialog forms where space is limited
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  PilcrowLeft,
  PilcrowRight,
  Undo,
  Redo,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { BlockTextDirection } from './extensions/BlockTextDirection';

interface CompactRichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
}

interface ToolbarButtonProps {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  label?: string;
  children: React.ReactNode;
}

function ToolbarButton({ active, disabled, onClick, label, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={label}
      title={label}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={cn(
        'h-6 w-6 rounded flex items-center justify-center transition-colors',
        'hover:bg-accent text-muted-foreground hover:text-foreground',
        'disabled:opacity-30 disabled:pointer-events-none',
        active && 'bg-accent text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function CompactToolbar({ editor }: { editor: Editor }) {
  const { t } = useTranslation('common');
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  return (
    <div className="flex items-center gap-0.5 px-1.5 py-1 border-b bg-muted/30">
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        <Undo className="h-3 w-3" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        <Redo className="h-3 w-3" />
      </ToolbarButton>

      <div className="w-px h-4 bg-border mx-0.5" />

      <ToolbarButton
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-3 w-3" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-3 w-3" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="h-3 w-3" />
      </ToolbarButton>

      <div className="w-px h-4 bg-border mx-0.5" />

      <ToolbarButton
        // eslint-disable-next-line i18next/no-literal-string -- TipTap node name, not user-facing
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-3 w-3" />
      </ToolbarButton>
      <ToolbarButton
        // eslint-disable-next-line i18next/no-literal-string -- TipTap node name, not user-facing
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-3 w-3" />
      </ToolbarButton>

      <div className="w-px h-4 bg-border mx-0.5" />

      <ToolbarButton
        active={editor.isActive({ dir: 'ltr' })}
        label={t('editor.textDirectionLtr')}
        onClick={() => editor.chain().focus().toggleTextDirection('ltr').run()}
      >
        <PilcrowLeft className="h-3 w-3" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive({ dir: 'rtl' })}
        label={t('editor.textDirectionRtl')}
        onClick={() => editor.chain().focus().toggleTextDirection('rtl').run()}
      >
        <PilcrowRight className="h-3 w-3" />
      </ToolbarButton>

      <div className="w-px h-4 bg-border mx-0.5" />

      <ToolbarButton
        active={editor.isActive('link')}
        label={t('editor.insertLink')}
        onClick={setLink}
      >
        <LinkIcon className="h-3 w-3" />
      </ToolbarButton>
    </div>
  );
}

export function CompactRichTextEditor({
  value,
  onChange,
  placeholder = '',
  minHeight = 80,
  disabled = false,
}: CompactRichTextEditorProps) {
  const [isFocused, setIsFocused] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      BlockTextDirection,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className={cn(
        'rounded-md border bg-background overflow-hidden transition-colors',
        isFocused && 'ring-1 ring-ring border-ring',
        disabled && 'opacity-60',
      )}
    >
      {!disabled && isFocused && <CompactToolbar editor={editor} />}
      <div style={{ minHeight }}>
        <EditorContent
          editor={editor}
          dir="auto"
          className={cn(
            'px-3 py-2 text-sm',
            'prose prose-sm dark:prose-invert max-w-none',
            '[&_.ProseMirror]:outline-none',
            '[&_.ProseMirror]:min-h-[inherit]',
            '[&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
            '[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left',
            '[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground',
            '[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none',
            '[&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0',
            '[&_.ProseMirror_a]:text-primary',
            '[&_.ProseMirror_a]:underline',
            '[&_.ProseMirror_ul]:ps-6',
            '[&_.ProseMirror_ol]:ps-6',
          )}
        />
      </div>
    </div>
  );
}
